import React, { useEffect, useState } from 'react'
import { Routes, Route, Navigate, useNavigate, useLocation, Link } from 'react-router-dom'
import { supabase } from './lib/supabase'
import { Toaster, toast } from 'sonner'
import {
    LogIn, ShieldCheck, Activity, Lock, Unlock, RefreshCw, Plus, Trash2,
    FileText, GitBranch, Home, Radio, AlertTriangle, BarChart3, Terminal,
    Send, ChevronRight, Menu, X, Power, FileSignature, Eye, ShieldAlert,
    ToggleRight, Zap, Gauge, Cpu, Globe, Accessibility, Package, KeyRound,
    Fingerprint, Hash, Shield as ShieldIcon, RotateCcw
} from 'lucide-react'

// Core
import DashboardPage from './pages/core/DashboardPage'
import LicensesPage from './pages/core/LicensesPage'
import VersionsPage from './pages/core/VersionsPage'
import DocsPage from './pages/core/DocsPage'

// Monitoring
import TelemetryPage from './pages/monitoring/TelemetryPage'
import ErrorsPage from './pages/monitoring/ErrorsPage'
import PerformancePage from './pages/monitoring/PerformancePage'

// Security
import SecurityPage from './pages/security/SecurityPage'
import EncryptionPage from './pages/security/EncryptionPage'
import IntegrityPage from './pages/security/IntegrityPage'
import CspPage from './pages/security/CspPage'
import WatermarkPage from './pages/security/WatermarkPage'
import SecretsPage from './pages/security/SecretsPage'

// Control
import CommandsPage from './pages/control/CommandsPage'
import FeatureFlagsPage from './pages/control/FeatureFlagsPage'
import CircuitBreakerPage from './pages/control/CircuitBreakerPage'
import WebSocketsPage from './pages/control/WebSocketsPage'

// Infra
import WasmPage from './pages/infra/WasmPage'
import ServiceWorkerPage from './pages/infra/ServiceWorkerPage'
import BundlePage from './pages/infra/BundlePage'
import SnapshotsPage from './pages/infra/SnapshotsPage'

// Legal
import ContractsPage from './pages/legal/ContractsPage'
import TransparencyPage from './pages/legal/TransparencyPage'
import AccessibilityPage from './pages/legal/AccessibilityPage'

// Re-export componentes UI para compatibilidad
// Componentes UI
import { Button, Input, Card, Badge } from './components/UI'
export { Button, Input, Card, Badge }

// ==========================================
// LOGIN PAGE
// ==========================================

function Login() {
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [loading, setLoading] = useState(false)
    const navigate = useNavigate()

    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => {
            if (session) navigate('/')
        })
    }, [])

    const handleLogin = async (e) => {
        e.preventDefault()
        setLoading(true)
        const { error } = await supabase.auth.signInWithPassword({ email, password })
        if (error) toast.error(error.message)
        else {
            toast.success('Bienvenido a la Torre de Control')
            navigate('/')
        }
        setLoading(false)
    }

    return (
        <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-900/20 via-obsidian to-obsidian"></div>
            <div className="w-full max-w-md relative z-10 p-6">
                <div className="text-center mb-8">
                    <div className="w-20 h-20 bg-gradient-to-br from-neon-blue to-indigo-600 rounded-2xl mx-auto flex items-center justify-center shadow-2xl shadow-neon-blue/30 mb-6">
                        <ShieldCheck size={40} className="text-white" />
                    </div>
                    <h1 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-500">
                        ANTIGRAVITY
                    </h1>
                    <p className="text-neon-blue tracking-[0.3em] text-sm font-medium mt-2">CONTROL TOWER</p>
                </div>
                <Card>
                    <form onSubmit={handleLogin} className="space-y-6">
                        <Input label="Credencial de Acceso" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@antigravity.io" aria-label="Email de acceso" />
                        <Input label="Clave Maestra" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" aria-label="Contraseña" />
                        <Button type="submit" className="w-full justify-center text-lg py-3" disabled={loading}>
                            {loading ? 'Autenticando...' : 'Iniciar Protocolo'}
                        </Button>
                    </form>
                </Card>
            </div>
        </div>
    )
}

// ==========================================
// SIDEBAR NAVIGATION
// ==========================================

import { NAV_GROUPS } from './config/navItems'
import { ChevronDown } from 'lucide-react'

function Sidebar() {
    const location = useLocation()
    const navigate = useNavigate()
    const [collapsed, setCollapsed] = useState(false)
    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
        const initial: Record<string, boolean> = {}
        NAV_GROUPS.forEach(g => { initial[g.label] = g.defaultOpen ?? false })
        return initial
    })

    const toggleGroup = (label: string) => {
        setOpenGroups(prev => ({ ...prev, [label]: !prev[label] }))
    }

    const handleLogout = async () => {
        await supabase.auth.signOut()
        navigate('/login')
    }

    // Auto-abrir grupo activo
    useEffect(() => {
        NAV_GROUPS.forEach(g => {
            if (g.items.some(i => i.path === location.pathname)) {
                setOpenGroups(prev => ({ ...prev, [g.label]: true }))
            }
        })
    }, [location.pathname])

    return (
        <aside className={`${collapsed ? 'w-16' : 'w-56'} h-screen bg-charcoal border-r border-white/5 flex flex-col transition-all duration-200 flex-shrink-0 sticky top-0`}>
            {/* Logo */}
            <div className="h-16 flex items-center justify-between px-3 border-b border-white/5">
                {!collapsed && (
                    <div className="flex items-center gap-2">
                        <ShieldCheck size={22} className="text-neon-blue" />
                        <span className="text-white font-bold text-sm tracking-wide">CONTROL TOWER</span>
                        <span className="text-[10px] text-gray-500 font-mono tracking-widest">v4.7.0</span>
                    </div>
                )}
                <button
                    onClick={() => setCollapsed(!collapsed)}
                    className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/5 transition-colors"
                    aria-label={collapsed ? 'Expandir sidebar' : 'Colapsar sidebar'}
                >
                    {collapsed ? <Menu size={18} /> : <X size={18} />}
                </button>
            </div>

            {/* Nav Groups */}
            <nav className="flex-1 py-2 px-2 overflow-y-auto">
                {NAV_GROUPS.map(group => {
                    const isOpen = openGroups[group.label]
                    const hasActive = group.items.some(i => i.path === location.pathname)

                    return (
                        <div key={group.label} className="mb-1">
                            {/* Group header */}
                            {!collapsed && (
                                <button
                                    onClick={() => toggleGroup(group.label)}
                                    className={`w-full flex items-center justify-between px-3 py-1.5 rounded-md text-[11px] font-semibold uppercase tracking-wider transition-colors
                                        ${hasActive ? 'text-neon-blue' : 'text-gray-500 hover:text-gray-300'}
                                    `}
                                >
                                    <span>{group.emoji} {group.label}</span>
                                    <ChevronDown size={12} className={`transition-transform duration-200 ${isOpen ? '' : '-rotate-90'}`} />
                                </button>
                            )}

                            {/* Group items */}
                            {(collapsed || isOpen) && (
                                <div className={`space-y-0.5 ${collapsed ? 'mt-1' : ''}`}>
                                    {group.items.map(item => {
                                        const isActive = location.pathname === item.path
                                        const Icon = item.icon
                                        return (
                                            <Link
                                                key={item.path}
                                                to={item.path}
                                                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150
                                                    ${isActive
                                                        ? 'bg-neon-blue/10 text-neon-blue border border-neon-blue/20'
                                                        : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
                                                    }
                                                `}
                                                title={collapsed ? item.label : undefined}
                                            >
                                                <Icon size={16} className="flex-shrink-0" />
                                                {!collapsed && <span className="text-[13px]">{item.label}</span>}
                                            </Link>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    )
                })}
            </nav>

            {/* Logout */}
            <div className="p-2 border-t border-white/5">
                <button
                    onClick={handleLogout}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-500 hover:text-red-400 hover:bg-red-500/5 transition-colors w-full border border-transparent`}
                    aria-label="Cerrar sesión"
                >
                    <Power size={18} className="flex-shrink-0" />
                    {!collapsed && <span>Salir</span>}
                </button>
            </div>
        </aside>
    )
}

// ==========================================
// APP SHELL (Layout Principal)
// ==========================================

function AppShell({ children }) {
    return (
        <div className="flex min-h-screen bg-obsidian text-gray-100 font-sans selection:bg-neon-blue/30">
            <Sidebar />
            <main className="flex-1 min-h-screen overflow-x-hidden">
                <div className="p-4 lg:p-8 max-w-[1920px] mx-auto space-y-6">
                    {children}
                </div>
            </main>
        </div>
    )
}

// ==========================================
// PRIVATE ROUTE
// ==========================================

function PrivateRoute({ children }) {
    const [session, setSession] = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => {
            setSession(session)
            setLoading(false)
        })
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            setSession(session)
        })
        return () => subscription.unsubscribe()
    }, [])

    if (loading) return <div className="min-h-screen bg-obsidian flex items-center justify-center text-white">Cargando Torre...</div>
    if (!session) return <Navigate to="/login" />
    return children
}

// ==========================================
// MAIN APP & ROUTING
// ==========================================

export default function App() {
    return (
        <>
            <Toaster position="top-center" theme="dark" />
            <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/*" element={
                    <PrivateRoute>
                        <AppShell>
                            <Routes>
                                <Route path="/" element={<DashboardPage />} />
                                <Route path="/licenses" element={<LicensesPage />} />
                                <Route path="/telemetry" element={<TelemetryPage />} />
                                <Route path="/errors" element={<ErrorsPage />} />
                                <Route path="/commands" element={<CommandsPage />} />
                                <Route path="/contracts" element={<ContractsPage />} />
                                <Route path="/transparency" element={<TransparencyPage />} />
                                <Route path="/security" element={<SecurityPage />} />
                                <Route path="/feature-flags" element={<FeatureFlagsPage />} />
                                <Route path="/circuit-breaker" element={<CircuitBreakerPage />} />
                                <Route path="/performance" element={<PerformancePage />} />
                                <Route path="/websockets" element={<WebSocketsPage />} />
                                <Route path="/wasm" element={<WasmPage />} />
                                <Route path="/service-worker" element={<ServiceWorkerPage />} />
                                <Route path="/encryption" element={<EncryptionPage />} />
                                <Route path="/accessibility" element={<AccessibilityPage />} />
                                <Route path="/bundle" element={<BundlePage />} />
                                <Route path="/secrets" element={<SecretsPage />} />
                                <Route path="/watermark" element={<WatermarkPage />} />
                                <Route path="/integrity" element={<IntegrityPage />} />
                                <Route path="/csp" element={<CspPage />} />
                                <Route path="/snapshots" element={<SnapshotsPage />} />
                                <Route path="/versions" element={<VersionsPage />} />
                                <Route path="/docs" element={<DocsPage />} />
                            </Routes>
                        </AppShell>
                    </PrivateRoute>
                } />
            </Routes>
        </>
    )
}
